import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MagnifyingGlassIcon,
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon, 
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import jobAPI from '../services/jobAPI';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useJobRestrictions } from '../contexts/JobRestrictionContext';
import { formatDate as formatDateUtil, formatTime as formatTimeUtil } from '../utils/dateUtils';
import { milesInputToNumber, isPartialMilesInput } from '../utils/mileageInputUtils';

const JobSearch = () => {
  const navigate = useNavigate();
  const { canAcceptJobs, showJobAcceptanceBlocked } = useJobRestrictions();
  const { profile } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showMileagePrompt, setShowMileagePrompt] = useState(false);
  const [showMileageFields, setShowMileageFields] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [mileageMilesInput, setMileageMilesInput] = useState('0');
  const mileageRequested = milesInputToNumber(mileageMilesInput);
  const [mileageRate, setMileageRate] = useState(0.70);
  const [mileagePromptLoading, setMileagePromptLoading] = useState(false);
  const FEDERAL_MILEAGE_CAP = 0.72;
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchRadius, setSearchRadius] = useState(null); // null = use profile setting
  const [tempRadius, setTempRadius] = useState(null); // For slider drag preview
  const [filters, setFilters] = useState({
    language: '',
    service_type: '',
    location: '',
    date_from: '',
    date_to: '',
    remote_only: false
  });

  useEffect(() => {
    loadJobs();
  }, [filters, currentPage, searchRadius]);

  useEffect(() => {
    if (!showMileagePrompt) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showMileagePrompt]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 12,
        ...filters
      };
      
      // Add interpreter_id for matching if profile exists
      if (profile?.id) {
        params.interpreter_id = profile.id;
        console.log('Loading jobs for interpreter:', profile.id);
        console.log('Interpreter profile:', {
          languages: profile.languages,
          service_types: profile.service_types,
          service_radius_miles: profile.service_radius_miles,
          location: `${profile.city}, ${profile.state_name}`
        });
      }
      
      // Add radius override if user changed it from profile setting
      if (searchRadius !== null && profile?.id) {
        params.radius = searchRadius;
      }
      
      console.log('Calling getAvailableJobs with params:', params);
      const response = await jobAPI.getAvailableJobs(params);
      console.log('Jobs response:', response.data);
      console.log('Jobs array:', response.data.data.jobs);
      console.log('Job count:', response.data.data.jobs.length);
      if (response.data.data.jobs.length > 0) {
        console.log('Job numbers:', response.data.data.jobs.map(j => j.job_number));
      }
      setJobs(response.data.data.jobs);
      setTotalPages(response.data.data.pagination.total_pages);
    } catch (error) {
      console.error('Error loading jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleJobAction = async (jobId, action, data = {}) => {
    try {
      let response;
      switch (action) {
        case 'available':
          // Show mileage prompt before indicating availability
          setShowMileagePrompt(true);
          setShowMileageFields(false);
          setSelectedJobId(jobId);
          return; // Don't proceed with availability yet
        case 'not_available':
          response = await jobAPI.indicateNotAvailable(jobId, data.reason || 'Not available');
          toast.success('Response recorded. Thank you for letting us know.');
          break;
        case 'accept':
          // Check if user can accept jobs (no overdue reports)
          if (!canAcceptJobs()) {
            showJobAcceptanceBlocked();
            return;
          }
          // Show mileage prompt before accepting
          setShowMileagePrompt(true);
          setShowMileageFields(false);
          setSelectedJobId(jobId);
          return; // Don't proceed with acceptance yet
        case 'decline':
          response = await jobAPI.declineJob(jobId, data);
          toast.success('Job declined');
          break;
        default:
          return;
      }
      
      // Reload jobs
      loadJobs();
    } catch (error) {
      console.error(`Error ${action}ing job:`, error);
      const errorMessage = error.response?.data?.message || `Failed to ${action} job`;
      toast.error(errorMessage);
    }
  };

  const handleMileageSubmit = async () => {
    if (!selectedJobId) return;
    
    setMileagePromptLoading(true);
    try {
      const effectiveRate = Math.min(FEDERAL_MILEAGE_CAP, Math.max(0, parseFloat(mileageRate) || 0.70));
      await jobAPI.indicateAvailability(selectedJobId, mileageRequested, effectiveRate);
      
      toast.success('Availability indicated! The admin will review and assign interpreters.');
      setShowMileagePrompt(false);
      setShowMileageFields(false);
      setSelectedJobId(null);
      setMileageMilesInput('0');
      setMileageRate(0.70);
      
      // Reload jobs
      loadJobs();
    } catch (error) {
      console.error('Error indicating availability:', error);
      toast.error(`Failed to indicate availability: ${error.response?.data?.message || error.message}`);
    } finally {
      setMileagePromptLoading(false);
    }
  };

  const handleNoMileage = async () => {
    if (!selectedJobId) return;
    
    setMileagePromptLoading(true);
    try {
      await jobAPI.indicateAvailability(selectedJobId, 0);
      
      toast.success('Availability indicated! The admin will review and assign interpreters.');
      setShowMileagePrompt(false);
      setShowMileageFields(false);
      setSelectedJobId(null);
      setMileageMilesInput('0');
      setMileageRate(0.70);
      
      // Reload jobs
      loadJobs();
    } catch (error) {
      console.error('Error indicating availability:', error);
      toast.error(`Failed to indicate availability: ${error.response?.data?.message || error.message}`);
    } finally {
      setMileagePromptLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      language: '',
      service_type: '',
      location: '',
      date_from: '',
      date_to: '',
      remote_only: false
    });
    setCurrentPage(1);
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(value => {
    if (typeof value === 'boolean') return value === true;
    return value !== '';
  }).length;

  // Quick filter helpers
  const setQuickFilter = (type) => {
    const today = new Date().toISOString().split('T')[0];
    const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    switch (type) {
      case 'today':
        setFilters(prev => ({ ...prev, date_from: today, date_to: today }));
        break;
      case 'this_week':
        setFilters(prev => ({ ...prev, date_from: today, date_to: nextWeek }));
        break;
      case 'remote':
        setFilters(prev => ({ ...prev, remote_only: !prev.remote_only }));
        break;
      case 'clear':
        clearFilters();
        break;
      default:
        break;
    }
  };

  // Use imported date utilities with custom options for this page
  const formatDate = (dateString) => {
    return formatDateUtil(dateString, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return formatTimeUtil(timeString);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const calculateEarnings = (job) => {
    // Backend resolves area-specific rates per job location
    if (job?.estimated_earnings) {
      return parseFloat(job.estimated_earnings);
    }

    const totalAmount = parseFloat(job.total_amount) || 0;
    if (totalAmount > 0) {
      return totalAmount;
    }

    if (job?.hourly_rate && job.estimated_duration_minutes) {
      const hours = job.estimated_duration_minutes / 60;
      const minimumHours = job.interpreter_minimum_hours
        ? parseFloat(job.interpreter_minimum_hours)
        : null;
      const billableHours = minimumHours ? Math.max(hours, minimumHours) : hours;
      return parseFloat(job.hourly_rate) * billableHours;
    }

    // Last resort: profile default rates (no per-job location context)
    if (profile?.service_rates && job.estimated_duration_minutes) {
      const serviceRate = profile.service_rates.find(
        (rate) => rate.service_type_id === job.service_type_id
      );

      if (serviceRate?.rate_amount) {
        const hours = job.estimated_duration_minutes / 60;
        const rateUnit = (serviceRate.rate_unit || 'hours').toLowerCase();

        if (rateUnit === 'minutes') {
          return parseFloat(serviceRate.rate_amount) * job.estimated_duration_minutes;
        }

        const blockMatch = rateUnit.match(/^(\d+)hours?$/);
        const effectiveRate = blockMatch
          ? parseFloat(serviceRate.rate_amount) / parseInt(blockMatch[1], 10)
          : parseFloat(serviceRate.rate_amount);
        return effectiveRate * hours;
      }
    }

    return 0;
  };

  const formatJobRateLabel = (job) => {
    if (job.interpreter_original_rate_amount != null && job.interpreter_rate_unit) {
      const amount = Number(job.interpreter_original_rate_amount);
      const unit = job.interpreter_rate_unit;
      if (unit === 'minutes') return `${formatCurrency(amount)}/min`;
      if (unit === '3hours') return `${formatCurrency(amount)}/3 hours`;
      if (unit === '6hours') return `${formatCurrency(amount)}/6 hours`;
      if (unit === 'word') return `${formatCurrency(amount)}/word`;
      return `${formatCurrency(amount)}/hour`;
    }
    if (job.hourly_rate) {
      return `${formatCurrency(job.hourly_rate)}/hour`;
    }
    if (profile?.service_rates) {
      const serviceRate = profile.service_rates.find(
        (rate) => rate.service_type_id === job.service_type_id
      );
      if (serviceRate?.rate_amount && serviceRate.rate_unit) {
        const isLegalOrVideo =
          serviceRate.service_type_code === 'legal' || serviceRate.service_type_code === 'video';
        const showPer3Hours = isLegalOrVideo && serviceRate.rate_unit === 'hours';
        const asBlock = serviceRate.legal_video_display_as_block === true;
        const amount =
          showPer3Hours && !asBlock
            ? Number(serviceRate.rate_amount) * 3
            : Number(serviceRate.rate_amount);
        const unitDisplay = showPer3Hours ? '3 hours' : serviceRate.rate_unit;
        return `${formatCurrency(amount)}/${unitDisplay}`;
      }
    }
    return 'Rate not set';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'normal': return 'text-blue-600 bg-blue-100';
      case 'low': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getJobTypeIcon = (jobType) => {
    switch (jobType) {
      case 'medical': return <BuildingOfficeIcon className="h-4 w-4" />;
      case 'legal': return <BuildingOfficeIcon className="h-4 w-4" />;
      case 'business': return <BuildingOfficeIcon className="h-4 w-4" />;
      case 'education': return <BuildingOfficeIcon className="h-4 w-4" />;
      default: return <BuildingOfficeIcon className="h-4 w-4" />;
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Find Jobs</h1>
          <p className="mt-2 text-gray-600">Browse available interpretation opportunities</p>
          {profile?.service_radius_miles && (
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <MapPinIcon className="h-4 w-4 mr-1" />
              Showing jobs within {searchRadius !== null ? searchRadius : profile.service_radius_miles} miles of your location
            </div>
          )}
        </motion.div>

        {/* Search Radius Control */}
        {profile?.id && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-blue-600 mr-2" />
                <div>
                  <label className="text-sm font-medium text-gray-900">Search Radius</label>
                  <p className="text-xs text-gray-600">Adjust how far to search for jobs</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="10"
                  max="200"
                  step="5"
                  value={tempRadius !== null ? tempRadius : (searchRadius !== null ? searchRadius : profile.service_radius_miles)}
                  onChange={(e) => setTempRadius(parseInt(e.target.value))}
                  onMouseUp={(e) => {
                    const value = parseInt(e.target.value);
                    setSearchRadius(value === profile.service_radius_miles ? null : value);
                    setTempRadius(null);
                  }}
                  onTouchEnd={(e) => {
                    const value = parseInt(e.target.value);
                    setSearchRadius(value === profile.service_radius_miles ? null : value);
                    setTempRadius(null);
                  }}
                  className="w-48 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider:bg-blue-600"
                />
                <div className="text-right min-w-[80px]">
                  <span className="text-lg font-semibold text-blue-600">
                    {tempRadius !== null ? tempRadius : (searchRadius !== null ? searchRadius : profile.service_radius_miles)}
                  </span>
                  <span className="text-sm text-gray-600 ml-1">miles</span>
                  {searchRadius !== null && searchRadius !== profile.service_radius_miles && (
                    <button
                      onClick={() => {
                        setSearchRadius(null);
                        setTempRadius(null);
                      }}
                      className="block mt-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      Reset to {profile.service_radius_miles}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8"
        >
          {/* Quick Filters - Chips/Pills */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setQuickFilter('today')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filters.date_from === new Date().toISOString().split('T')[0] && filters.date_to === new Date().toISOString().split('T')[0]
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Today
              </button>
              <button
                onClick={() => setQuickFilter('this_week')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filters.date_from && filters.date_to && filters.date_to !== filters.date_from
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                This Week
              </button>
              <button
                onClick={() => setQuickFilter('remote')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filters.remote_only
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                Remote Only
              </button>
              {activeFilterCount > 0 && (
                <button
                  onClick={() => setQuickFilter('clear')}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                >
                  <XCircleIcon className="h-4 w-4 inline mr-1" />
                  Clear All ({activeFilterCount})
                </button>
              )}
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium mb-4"
          >
            <FunnelIcon className="h-4 w-4 mr-1" />
            {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
            {activeFilterCount > 0 && !showAdvancedFilters && (
              <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs">
                {activeFilterCount} active
              </span>
            )}
          </button>

          {/* Advanced Filters - Collapsible */}
          {showAdvancedFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4"
            >
              {/* Language Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language
                </label>
                <input
                  type="text"
                  placeholder="e.g., Spanish"
                  value={filters.language}
                  onChange={(e) => handleFilterChange('language', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Service Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Type
                </label>
                <input
                  type="text"
                  placeholder="e.g., Medical"
                  value={filters.service_type}
                  onChange={(e) => handleFilterChange('service_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Location Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  placeholder="e.g., Los Angeles"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </motion.div>
          )}

          {/* Results Count */}
          <div className="flex items-center text-sm text-gray-500 pt-4 border-t border-gray-200">
            <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
            {loading ? 'Searching...' : `${jobs.length} jobs found`}
          </div>
        </motion.div>

        {/* Jobs Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12">
              <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search criteria or check back later for new opportunities.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {jobs.map((job) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/job/${job.id}`)}
                  >
                    {/* Job Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                          {job.status === 'finding_interpreter' 
                            ? (job.service_type_name || 'Job Opportunity')
                            : (job.job_number || job.title)}
                        </h3>
                        <div className="flex items-center mt-2 space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(job.priority)}`}>
                            {job.priority} Priority
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {getJobTypeIcon(job.job_type)}
                            <span className="ml-1 capitalize">{job.job_type}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Job Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{job.description}</p>

                    {/* Job Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        {formatDate(job.scheduled_date)}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <ClockIcon className="h-4 w-4 mr-2" />
                        {formatTime(job.arrival_time || job.scheduled_time)} ({job.estimated_duration_minutes} min)
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        {job.is_remote ? (
                          <>
                            <GlobeAltIcon className="h-4 w-4 mr-2" />
                            Remote
                          </>
                        ) : (
                          <>
                            <MapPinIcon className="h-4 w-4 mr-2" />
                            {job.location_address || 
                              (job.location_city && job.location_state 
                                ? `${job.location_city}, ${job.location_state}` 
                                : 'Location TBD')}
                          </>
                        )}
                      </div>
                      {!job.is_remote && job.distance_miles !== null && job.distance_miles !== undefined && (
                        <div className="flex items-center text-sm text-blue-600">
                          <ArrowPathIcon className="h-4 w-4 mr-2" />
                          <span className="font-medium">{job.distance_miles.toFixed(1)} miles away</span>
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-500">
                        <span className="font-medium">Languages:</span>
                        <span className="ml-1">{job.source_language_name || job.language_name || 'N/A'} → {job.target_language_name || 'English'}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <span className="font-medium">Service:</span>
                        <span className="ml-1">{job.service_type_name}</span>
                      </div>
                    </div>

                    {/* Rate and Earnings */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-right">
                        <div className="text-lg font-semibold text-green-600">
                          {formatCurrency(calculateEarnings(job))}
                        </div>
                        <div className="text-xs text-gray-500">
                          {profile?.service_rates ? 'Your earnings' : 'per hour'}
                        </div>
                        {profile?.service_rates && (
                          <div className="text-xs text-gray-400">
                            {formatJobRateLabel(job)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                      {job.status === 'finding_interpreter' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleJobAction(job.id, 'not_available')}
                          >
                            <XCircleIcon className="h-4 w-4 mr-1" />
                            Not Available
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleJobAction(job.id, 'available')}
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            I'm Available
                          </Button>
                        </>
                      )}
                      {job.status === 'assigned' && job.assigned_interpreter_id && (
                        <span className="text-sm text-gray-500 px-3 py-2 bg-gray-100 rounded-md">
                          Job Assigned
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center space-x-2 mt-8">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}

              {/* Mileage Prompt Modal */}
              {showMileagePrompt && (
                <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black bg-opacity-50 touch-pan-y [-webkit-overflow-scrolling:touch]">
                  <div className="flex min-h-[100vh] min-h-dvh w-full items-center justify-center px-4 py-6 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1.25rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
                    <div className="relative w-full max-w-md shrink-0 rounded-lg bg-white p-5 shadow-lg sm:p-8">
                    {/* Close Button */}
                    <button
                      onClick={() => {
                        setShowMileagePrompt(false);
                        setShowMileageFields(false);
                        setSelectedJobId(null);
                        setMileageMilesInput('0');
                        setMileageRate(0.70);
                      }}
                      className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 sm:right-4 sm:top-4"
                      aria-label="Close"
                    >
                      <XCircleIcon className="h-7 w-7 sm:h-6 sm:w-6" />
                    </button>
                    
                    <div className="text-center mb-6">
                      <CheckCircleIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Indicate Your Availability
                      </h2>
                      <p className="text-gray-600 text-sm">
                        Do you need to be reimbursed for mileage to this job location?
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-red-700">
                              <strong>Please note:</strong> Mileage requests for assignments under a 25-mile radius will not be approved. Please avoid entering mileage unless it is truly over 25 miles from your location.
                            </p>
                          </div>
                        </div>
                      </div>
                      {!showMileageFields ? (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-800 text-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 leading-snug">
                            Tap <span className="font-semibold text-blue-900">Enter miles & rate</span> below to open the form. Miles and per-mile rate are filled in on the next step after you tap.
                          </p>
                          <div className="flex flex-col gap-2 sm:flex-row sm:space-x-3 sm:gap-0">
                            <button
                              type="button"
                              onClick={() => setShowMileageFields(true)}
                              disabled={mileagePromptLoading}
                              className="min-h-[48px] flex-1 touch-manipulation rounded-md border-2 border-blue-600 bg-white px-4 py-3 text-base font-semibold text-blue-900 shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:py-2.5 sm:text-sm"
                            >
                              Enter miles & rate
                            </button>
                            <button
                              type="button"
                              onClick={handleNoMileage}
                              disabled={mileagePromptLoading}
                              className="min-h-[48px] flex-1 touch-manipulation rounded-md bg-blue-600 px-4 py-3 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:py-2.5 sm:text-sm"
                            >
                              {mileagePromptLoading ? 'Submitting...' : 'Proceed with no mileage'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Miles to job location:
                            </label>
                            <input
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              value={mileageMilesInput}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (isPartialMilesInput(v)) setMileageMilesInput(v);
                              }}
                              className="w-full rounded-md border border-gray-300 px-3 py-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 sm:py-2"
                              placeholder="0"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Clear the field and enter your miles, or use Back if you do not need mileage.
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Rate per mile ($):
                            </label>
                            <input
                              type="number"
                              min="0"
                              max={FEDERAL_MILEAGE_CAP}
                              step="0.01"
                              value={mileageRate}
                              onChange={(e) => setMileageRate(Math.min(FEDERAL_MILEAGE_CAP, Math.max(0, parseFloat(e.target.value) || 0)))}
                              className="w-full rounded-md border border-gray-300 px-3 py-3 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 sm:py-2"
                              placeholder="0.70"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Federal cap: ${FEDERAL_MILEAGE_CAP}/mile.
                            </p>
                            {mileageRequested > 0 && (
                              <p className="text-sm text-gray-500 mt-1">
                                Estimated reimbursement: ${(mileageRequested * Math.min(FEDERAL_MILEAGE_CAP, mileageRate || 0.70)).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </>
                      )}

                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                              <strong>Important:</strong> If you request mileage reimbursement, your assignment will need admin approval before being confirmed.
                            </p>
                          </div>
                        </div>
                      </div>

                      {showMileageFields && (
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setShowMileageFields(false);
                              setMileageMilesInput('0');
                            }}
                            disabled={mileagePromptLoading}
                            className="min-h-[48px] flex-1 touch-manipulation rounded-md bg-gray-200 px-4 py-3 text-base font-medium text-gray-800 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 sm:py-2 sm:text-sm"
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            onClick={handleMileageSubmit}
                            disabled={mileagePromptLoading || mileageRequested <= 0}
                            className="min-h-[48px] flex-1 touch-manipulation rounded-md bg-blue-600 px-4 py-3 text-base font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:py-2 sm:text-sm"
                          >
                            {mileagePromptLoading ? 'Submitting...' : 'Request Mileage'}
                          </button>
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default JobSearch;
